import {
  AccordionSection,
  RadioInputSectionList,
  SearchInputProps,
  SelectDropdownInputOption,
} from '@va/ui/design-system';
import { isNil } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { AccordionSectionProps } from '../accordion-section/AccordionSection';

type AccordionSelectProps = AccordionSectionProps & {
  options: SelectDropdownInputOption[];
  selectedOption?: SelectDropdownInputOption;
  onChange: (option: SelectDropdownInputOption) => void;
  searchInputProps?: SearchInputProps;
  isLoading?: boolean;
  showSubtitle?: boolean;
  useCustomTitle?: boolean;
  useCustomSubtitle?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  disabledTooltip?: ReactNode;
  noSelectionErrorMessage?: string;
  hint?: string;
  floating?: boolean;
};

export const AccordionSelect: React.FC<AccordionSelectProps> = ({
  required,
  icon,
  bgColorClass,
  title,
  subtitle,
  options,
  selectedOption,
  onChange,
  searchInputProps,
  isLoading,
  titleTooltip,
  titleTooltipProps,
  showSubtitle = true,
  'data-testid': dataTestId,
  useCustomTitle,
  useCustomSubtitle,
  titleClassName,
  subtitleClassName,
  onOpen,
  onClose,
  isOpenExternal,
  toggleIsOpenExternal,
  disabled = false,
  disabledTooltip,
  noSelectionErrorMessage,
  floating = false,
  shouldCloseOnOutsideClick = false,
  size,
  hint,
  heightClass,
  className,
  wrapperClassName,
  arrowButtonClassName,
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(isOpenExternal);
  const isOpen = !isNil(isOpenExternal) ? isOpenExternal : isOpenInternal;

  const sectionSubtitle = useMemo(() => {
    if (!showSubtitle) return;
    return useCustomSubtitle && subtitle ? subtitle : (selectedOption?.value?.toString() ?? subtitle);
  }, [showSubtitle, useCustomSubtitle, subtitle, selectedOption]);

  const sectionTitle = useMemo(
    () => (useCustomTitle && title ? title : (selectedOption?.label ?? title)),
    [useCustomTitle, title, selectedOption],
  );

  const onInputChange = (option: SelectDropdownInputOption) => {
    onChange(option);
    onClose?.();
    setIsOpenInternal(false);
  };

  return (
    <AccordionSection
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      required={required}
      icon={icon}
      titleTooltipProps={titleTooltipProps}
      titleTooltip={selectedOption ? undefined : titleTooltip}
      bgColorClass={bgColorClass}
      title={sectionTitle}
      subtitle={sectionSubtitle}
      isOpenExternal={isOpen}
      toggleIsOpenExternal={() => {
        if (!isNil(isOpenExternal) && toggleIsOpenExternal) {
          toggleIsOpenExternal();
        } else {
          setIsOpenInternal((prev) => {
            if (prev) {
              onClose?.();
            } else {
              onOpen?.();
            }

            return !prev;
          });
        }
      }}
      data-testid={dataTestId}
      titleClassName={titleClassName}
      subtitleClassName={subtitleClassName}
      floating={floating}
      shouldCloseOnOutsideClick={shouldCloseOnOutsideClick}
      size={size}
      heightClass={heightClass}
      className={className}
      wrapperClassName={wrapperClassName}
      arrowButtonClassName={arrowButtonClassName}
    >
      <RadioInputSectionList
        id={`${title ?? 'accordion-select'}-list`}
        isLoading={isLoading}
        searchInputProps={searchInputProps}
        options={options}
        selectedOption={selectedOption}
        onChange={onInputChange}
        noSelectionErrorMessage={noSelectionErrorMessage}
        floating={floating}
        hint={hint}
      />
    </AccordionSection>
  );
};
